import getLang from 'I18n/utils/getLang';
import { AppContextType } from 'marketplace-ui-common/types';
import { ProductType } from 'marketplace-ui-common/types/products';
import { PublicCombosResponse } from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/PublicScopeGapsService';
import { useGetSatisfyingCombosBatchQuery as usePublicGetSatisfyingCombosBatchQuery } from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/PublicScopeGapsService.hooks';
import {
  CombosResponse,
  ScopesRequest,
} from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService';
import { useGetFullSatisfyingCombosBatchQuery as useAuthGetSatisfyingCombosBatchQuery } from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { UnifiedDetailsFeature } from 'marketplace-ui-common/types/index';

interface UseFeaturePlanCombinationsProps {
  appContext: AppContextType;
  features: UnifiedDetailsFeature[];
  productType: ProductType;
}

export function useFeaturePlanCombinations({
  appContext,
  features,
  productType,
}: UseFeaturePlanCombinationsProps) {
  const filteredFeatures =
    productType !== 'APP'
      ? features
      : features.filter((feature: UnifiedDetailsFeature) => {
          return (
            feature?.featureLocation?.includes('MARKETPLACE_LISTING') ?? false
          );
        });

  const requestPayloads: ScopesRequest[] = [];

  filteredFeatures.forEach((feature) => {
    const scopes =
      feature.featureInternalScopes
        ?.filter((scope: string) => scope !== '')
        .map((scope: string) => scope.replaceAll('_', '-').toLowerCase()) || [];
    const scopeGroups =
      feature.featureScopeGroups?.filter(
        (scopeGroup: string) => scopeGroup !== ''
      ) || [];

    requestPayloads.push({
      frontendLocale: getLang(),
      minimal: true,
      scopes,
      scopeGroups,
    });
  });

  const {
    data: publicData,
    loading: publicLoading,
    error: publicError,
  } = usePublicGetSatisfyingCombosBatchQuery({
    variables: {
      requests: requestPayloads,
    },
    skip: appContext !== 'public' || filteredFeatures.length === 0,
  });

  const {
    data: appData,
    loading: appLoading,
    error: appError,
  } = useAuthGetSatisfyingCombosBatchQuery({
    variables: {
      requests: requestPayloads,
    },
    skip: appContext === 'public' || filteredFeatures.length === 0,
  });

  const data = appData || publicData;
  const loading = publicLoading || appLoading;
  const error = publicError || appError;

  const result: Record<string, PublicCombosResponse | CombosResponse> = {};

  data?.results.forEach((featureData, index) => {
    if (index < filteredFeatures.length) {
      result[filteredFeatures[index].featureName] = featureData;
    }
  });

  return { result, loading, error };
}
